import {Button, Modal, Alert, Popconfirm, Select, Icon} from "antd"
import React, {useEffect, useRef, useState} from "react"
import {releaseBasketByOrders} from '../../../services/api/mergerApi'

const TOKEN_SEPARATORS = [',', ' ', '\n', '\r', '\t', ';']

function ModalReleaseBaskets(props) {
    const {showModalReleaseBaskets, onClose} = {...props}

    const [orderNumbers, setOrderNumbers] = useState([])
    const [, setTypingTick] = useState(0)
    const [force, setForce] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [releasedBaskets, setReleasedBaskets] = useState([])
    const [submitted, setSubmitted] = useState(false)

    const $inputOrders = useRef(null)
    const $typingSyncScheduled = useRef(false)

    const parseOrderNumbers = (values) => {
        const orders = values.map(value => `${value}`.trim()).filter(value => !!value && !isNaN(value))

        return orders.filter((order, index) => orders.indexOf(order) === index)
    }

    useEffect(() => {
        const focusTimer = setTimeout(() => {
            $inputOrders.current && $inputOrders.current.focus()
        }, 300)

        return () => clearTimeout(focusTimer)
    }, [])

    const releaseBaskets = async () => {
        if (loading) {
            return
        }

        // rc-select chỉ commit mã đang gõ khi gặp separator hoặc lúc blur (setTimeout),
        // nên đọc thẳng ô nhập lúc submit để không phải bấm 2 lần
        const orders = getEffectiveOrders()

        setOrderNumbers(orders)
        clearTypingOrder()
        blurOrders()
        setLoading(true)
        setError('')
        setReleasedBaskets([])
        setSubmitted(false)

        await releaseBasketByOrders({
            order_numbers: orders,
            ...(force ? {force} : {}),
            update: true
        }).then((res) => {
            const {
                data,
                success,
                error: responseError
            } = res.data

            if (responseError || success === false) {
                return setError(getErrorMessage(responseError))
            }

            const {baskets = []} = {...data}
            setReleasedBaskets(baskets)
            setSubmitted(true)
        }).catch(e => {
            setError(getErrorMessage(e))
        }).finally(() => {
            setLoading(false)
        })
    }

    // API có thể trả error dạng object; Alert chỉ render được string
    const getErrorMessage = (responseError) => {
        if (!responseError) {
            return 'Xóa giỏ không thành công'
        }

        return typeof responseError === 'string'
            ? responseError
            : (responseError.message || 'Xóa giỏ không thành công')
    }

    // Ép render lại sau khi rc-select đã commit xong để đọc lại ô nhập.
    // KHÔNG cache giá trị đang gõ: rc-select (9.2.3, node_modules/rc-select/lib/Select.js) có đường
    // làm rỗng ô nhập mà không bắn onSearch (tokenize :137 dùng fireSearch=false, blur :438 gán thẳng
    // state.inputValue) và cũng có thể không bắn onChange (token trùng thì getValueByInput trả undefined, :788),
    // cache sẽ giữ lại một mã mà ô nhập đã trống -> mã ma trong payload.
    // Microtask cũng cần cho onSearch vì rc-select gọi nó từ trong setState updater của chính nó.
    const scheduleTypingSync = () => {
        if ($typingSyncScheduled.current) {
            return
        }

        $typingSyncScheduled.current = true
        Promise.resolve().then(() => {
            $typingSyncScheduled.current = false
            setTypingTick(tick => tick + 1)
        })
    }

    const onChangeOrders = (values) => {
        if (loading) {
            // đang gọi API: bỏ qua thay đổi, render lại để rc-select đồng bộ lại value từ props
            return scheduleTypingSync()
        }

        setOrderNumbers(parseOrderNumbers(values))
        scheduleTypingSync()
    }

    const onSearchOrders = () => {
        scheduleTypingSync()
    }

    const onClearOrders = () => {
        if (loading) {
            return
        }

        // dọn luôn ô nhập, nếu không blur timer (10ms) sẽ commit chữ đang gõ thành tag ngay sau khi xóa hết
        clearTypingOrder()
        setOrderNumbers([])
    }

    // Ô nhập của Select là input 1 dòng: browser tự bỏ hoặc đổi \r\n thành khoảng trắng khi
    // paste, nên tokenSeparators không bao giờ thấy ký tự xuống dòng. Tự xử lý paste nhiều dòng
    // (paste 1 cột từ Excel) để không bị dính thành 1 mã đơn bịa ra.
    const onPasteOrders = (e) => {
        const text = e.clipboardData ? e.clipboardData.getData('text') : ''

        if (loading || !/[\r\n]/.test(text)) {
            return
        }

        e.preventDefault()
        setOrderNumbers(parseOrderNumbers([...orderNumbers, ...text.split(/[\s,;]+/)]))
        clearTypingOrder()
    }

    const getRcSelect = () => {
        const select = $inputOrders.current

        return select && select.rcSelect ? select.rcSelect : null
    }

    // đọc thẳng ô nhập của rc-select, nguồn duy nhất đáng tin cho mã đơn đang gõ dở
    const getTypingOrder = () => {
        const rcSelect = getRcSelect()
        const input = rcSelect && rcSelect.getInputDOMNode ? rcSelect.getInputDOMNode() : null
        const typing = input ? `${input.value}`.trim() : ''

        return !!typing && !isNaN(typing) && !orderNumbers.includes(typing) ? typing : ''
    }

    const blurOrders = () => {
        const select = $inputOrders.current

        if (select && select.blur) {
            select.blur()
        }
    }

    const clearTypingOrder = () => {
        const rcSelect = getRcSelect()

        if (rcSelect && rcSelect.setInputValue) {
            rcSelect.setInputValue('')
        }
    }

    const getEffectiveOrders = () => {
        const typing = getTypingOrder()

        return typing ? [...orderNumbers, typing] : orderNumbers
    }

    const totalOrders = getEffectiveOrders().length
    const canRelease = !!totalOrders && !loading

    return <Modal visible={showModalReleaseBaskets}
                  title={'Xóa giỏ theo danh sách đơn'}
                  className="ModalReleaseBasket"
                  width={860}
                  style={{top: 40}}
                  closable={!loading}
                  maskClosable={false}
                  keyboard={!loading}
                  onCancel={onClose}
                  footer={
                      <div className="modal-actions">
                          <span className="summary">
                              {
                                  totalOrders
                                      ? `${totalOrders} đơn trong danh sách`
                                      : 'Chưa có đơn nào trong danh sách'
                              }
                          </span>
                          <span className="buttons">
                              <Button size='large' disabled={loading} onClick={onClose}>
                                  Hủy
                              </Button>
                              <Popconfirm
                                  title={`Chắc chắn xóa giỏ của ${totalOrders} đơn?`}
                                  overlayClassName="confirm-release-baskets"
                                  placement="topRight"
                                  okText="Xóa giỏ"
                                  okType="danger"
                                  cancelText="Hủy"
                                  disabled={!canRelease}
                                  onConfirm={releaseBaskets}
                              >
                                  <Button
                                      type="primary"
                                      size='large'
                                      loading={loading}
                                      disabled={!canRelease}
                                  >
                                      Xóa giỏ
                                  </Button>
                              </Popconfirm>
                          </span>
                      </div>
                  }
    >
        <div className="mb-3">
            <div className={`field-head${loading ? ' is-loading' : ''}`}>
                <label className="FieldName">Danh sách đơn</label>
                {
                    !!totalOrders && <span className="orders-count">
                        {totalOrders} đơn
                        <span className="clear-orders" onClick={onClearOrders}>Xóa hết</span>
                    </span>
                }
            </div>
            <div className={`input-orders-wrap${loading ? ' is-loading' : ''}`} onPaste={onPasteOrders}>
                <Select mode="tags"
                        className="input-orders"
                        ref={$inputOrders}
                        value={orderNumbers}
                        tokenSeparators={TOKEN_SEPARATORS}
                        placeholder={'Dán danh sách đơn: 10011976, 10011990, 10011994...'}
                        dropdownStyle={{display: 'none'}}
                        notFoundContent={null}
                        onSearch={onSearchOrders}
                        onChange={onChangeOrders}/>
                {loading && <Icon type="loading" className="input-orders-spinner"/>}
            </div>
            <p className="description mb-0">
                Danh sách đơn ngăn cách bằng dấu phảy, dấu cách hoặc xuống dòng · bấm dấu x trên từng tag để bỏ đơn
            </p>
        </div>
        {
            error && <div className="mb-3 error"><Alert message={error} type="error" showIcon={true}/></div>
        }
        {
            !!releasedBaskets.length && <div className="mb-3 list-panel released">
                <div className="panel-head">
                    <span>Giỏ đã xóa</span>
                    <span className="count">{releasedBaskets.length}</span>
                </div>
                <div className="panel-body">
                    {
                        releasedBaskets.map(basket => <a key={basket._id}
                                                         className="number-tag basket-number"
                                                         href={`/baskets/${basket._id}`}
                                                         target="_blank"
                                                         rel="noopener noreferrer">{basket.number}</a>)
                    }
                </div>
            </div>
        }
        {
            submitted && !releasedBaskets.length &&
            <div className="mb-3"><Alert message="Không có giỏ nào được xóa" type="info" showIcon={true}/></div>
        }

        <div className="mb-0 field-check">
            <label>
                <input type="checkbox"
                       name="force"
                       checked={force}
                       disabled={loading}
                       onChange={(e) => setForce(e.target.checked)}/>
                <span>Xóa cả những giỏ chưa hoàn thành</span>
            </label>
        </div>

    </Modal>
}

export default ModalReleaseBaskets
